import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  UserPlus,
  ChevronDown,
  Check,
  Phone,
  Mail,
  Users,
  UserCheck,
  ShieldCheck,
  Lock,
  Unlock,
  SlidersHorizontal,
} from 'lucide-react';
import InviteMemberModal from './invite-modal';
import BlockMemberModal from './block-modal';
import { useToast } from '../../Utils/toastContext';

/* ================================================================== */
/*  API  →  UI  MAPPING                                               */
/*                                                                    */
/*  GET /api/v1/agency/members                                        */
/*    ?page=1&limit=10[&status=..][&search=..]                        */
/*    [&sort_by=..][&sort_order=..]                                   */
/*    →  { status, data: { members: [],                               */
/*                         counts: { all, invited, active, blocked }, */
/*                         total, limit, page, current_page,          */
/*                         total_pages, agency_id } }                 */
/*                                                                    */
/*  API field                →  UI field                              */
/*  ----------------------------------------------------------------  */
/*  user_id                  →  id / user_id (row key + block calls)  */
/*  full_name                →  name  (falls back to first+last)      */
/*  first_name + last_name   →  initial (avatar initials)             */
/*  email_address            →  email                                 */
/*  country_code+mobile_number →  mobile (under the email)            */
/*  member_state             →  status badge (active/invited/blocked) */
/*  status.label             →  fallback when member_state is missing */
/*                              (pending → invited)                   */
/*  is_agency_owner          →  "Owner" chip + block action disabled  */
/*  block_reason             →  sub-line under the Blocked badge      */
/*  email_verified           →  Verified column (email tick)          */
/*  mobile_number_verified   →  Verified column (mobile tick)         */
/*  enforce_password_reset   →  kept on the row (temp-password flag)  */
/*  created_at               →  Joined column + "Invite sent X ago"   */
/*  updated_at               →  "Blocked X ago" sub-line              */
/*  unique_id                →  kept on the row                       */
/*  data.counts              →  summary cards + tab badges — ignores  */
/*                              search and the selected tab, so the   */
/*                              badges stay steady while typing       */
/*  data.total / total_pages →  pagination footer + page numbers      */
/*                                                                    */
/*  POST /api/v1/agency/members/invite                                */
/*    →  InviteMemberModal. 201 returns temp_password ONCE — shown in */
/*       the modal's success step, never stored on the frontend.      */
/*                                                                    */
/*  PUT /api/v1/agency/members/{user_id}/block      { reason }        */
/*  PUT /api/v1/agency/members/{user_id}/unblock    { reason? }       */
/*    →  BlockMemberModal. Both are PUT — there is no                 */
/*       reactivate_invitation permission, so unblocking restores     */
/*       access via /unblock rather than re-inviting.                 */
/*                                                                    */
/*  Guards, in order: agency role on the token → INVITATION           */
/*  permission (role 5 already holds it) → owner-only check in the    */
/*  controller (the owner row can never be blocked from the UI).      */
/* ================================================================== */

/* ------------------------------------------------------------------ */
/*  Member state → badge + avatar colours. The API's `member_state`   */
/*  is the source of truth (active / invited / blocked); status.label */
/*  is only a fallback for payloads without it.                       */
/* ------------------------------------------------------------------ */
const STATUS_BADGE = {
  active: 'bg-emerald-50 text-emerald-600 ring-emerald-200',
  invited: 'bg-amber-50 text-amber-600 ring-amber-200',
  blocked: 'bg-red-50 text-red-600 ring-red-200',
};

const STATE_BY_LABEL = {
  active: 'active',
  pending: 'invited',
  invited: 'invited',
  blocked: 'blocked',
};

const STATE_TEXT = {
  active: 'Active',
  invited: 'Invited',
  blocked: 'Blocked',
};

const STATE_COLOR = {
  active: '#10B981',
  invited: '#8B5CF6',
  blocked: '#EF4444',
};

const humanize = (s = '') =>
  String(s)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();

/* "2026-09-14T23:05:23.810564Z" → "2h ago" */
const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
};

/* "2026-09-14" → "14 Sep 2026" */
const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/* One API record → one table row */
const mapMember = (raw, index = 0) => {
  const r = raw || {};
  const firstName = r.first_name || '';
  const lastName = r.last_name || '';
  const fullName =
    r.full_name || `${firstName} ${lastName}`.trim() || 'Unnamed member';
  const state = r.member_state || STATE_BY_LABEL[r.status?.label] || 'invited';

  const initials =
    ((firstName[0] || '') + (lastName[0] || '')).toUpperCase() ||
    fullName
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .split(/\s+/)
      .map((w) => w[0] || '')
      .join('')
      .slice(0, 2)
      .toUpperCase() ||
    '??';

  return {
    id: r.user_id ?? index,
    user_id: r.user_id,
    name: fullName,
    email: r.email_address || '',
    mobile: [r.country_code, r.mobile_number].filter(Boolean).join(' '),
    /* state drives the badge; status kept for reference */
    state,
    status: { id: r.status?.id, label: r.status?.label || state },
    stateText: STATE_TEXT[state] ?? humanize(state),
    isOwner: !!r.is_agency_owner,
    blockReason: r.block_reason || '',
    emailVerified: !!r.email_verified,
    mobileVerified: !!r.mobile_number_verified,
    enforcePasswordReset: !!r.enforce_password_reset,
    unique_id: r.unique_id || '',
    invitedBy: r.invited_by_user_id,
    joined: shortDate(r.created_at),
    joinedAgo: relTime(r.created_at),
    updatedAt: r.updated_at,
    initial: initials,
    created_at: r.created_at,
    raw: r,
  };
};

/* Pull the member array out of whatever envelope the API uses */
const extractMembers = (response) => {
  const candidates = [
    response?.data?.members,
    response?.members,
    response?.data,
    response,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
};

/* Read total / total_pages from the response envelope:
     { data: { members, counts, total, limit, page,
               current_page, total_pages } }                       */
const numFrom = (response, keys) => {
  const scopes = [
    response?.data,
    response?.data?.meta,
    response?.data?.meta?.pagination,
    response?.data?.pagination,
    response?.meta,
    response?.pagination,
    response,
  ];
  for (const scope of scopes) {
    if (!scope || typeof scope !== 'object') continue;
    for (const k of keys) {
      if (typeof scope[k] === 'number') return scope[k];
    }
  }
  return undefined;
};

/* Whether any filter is currently applied (drives the chips row) */
const hasActiveFilters = (f) =>
  f.tab !== 'All' || f.query.trim() !== '' || !f.isDefaultSort;

/* Removable chip used in the active-filters row */
function FilterChip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1 rounded-lg border border-brand/30 bg-brand/[0.06] text-[11px] font-semibold text-brand">
      {label}
      <button
        onClick={onClear}
        title={`Remove filter: ${label}`}
        className="w-5 h-5 rounded flex items-center justify-center hover:bg-brand/15 transition-colors"
      >
        <X size={11} strokeWidth={2.4} />
      </button>
    </span>
  );
}

/* Status filter tabs. `status` param values: invited / active / blocked
   (pending also works; blocked wins — a blocked member never shows
   under active). "All" sends nothing. */
const TABS = ['All', 'Active', 'Invited', 'Blocked'];

const FILTER_BY_TAB = {
  All: 'all',
  Active: 'active',
  Invited: 'invited',
  Blocked: 'blocked',
};

const TAB_BY_FILTER = {
  all: 'All',
  active: 'Active',
  invited: 'Invited',
  blocked: 'Blocked',
};

/* Summary cards — driven by data.counts (ignores search + tab, so the
   numbers stay steady while someone types) */
const CARDS = [
  {
    key: 'all',
    tab: 'All',
    label: 'All members',
    icon: Users,
    color: '#375AE7',
    tint: 'rgba(55,90,231,0.10)',
  },
  {
    key: 'active',
    tab: 'Active',
    label: 'Active admins',
    icon: UserCheck,
    color: '#10B981',
    tint: 'rgba(16,185,129,0.12)',
  },
  {
    key: 'invited',
    tab: 'Invited',
    label: 'Invited',
    icon: Mail,
    color: '#8B5CF6',
    tint: 'rgba(139,92,246,0.12)',
  },
  {
    key: 'blocked',
    tab: 'Blocked',
    label: 'Blocked',
    icon: Lock,
    color: '#EF4444',
    tint: 'rgba(239,68,68,0.10)',
  },
];

/* sort_by + sort_order options (API: full_name / email_address /
   status / created_at, asc | desc — default created_at desc) */
const SORTS = [
  { label: 'Newest first', sort_by: 'created_at', sort_order: 'desc' },
  { label: 'Oldest first', sort_by: 'created_at', sort_order: 'asc' },
  { label: 'Name A–Z', sort_by: 'full_name', sort_order: 'asc' },
  { label: 'Name Z–A', sort_by: 'full_name', sort_order: 'desc' },
  { label: 'Email A–Z', sort_by: 'email_address', sort_order: 'asc' },
  { label: 'Status', sort_by: 'status', sort_order: 'asc' },
];

/* ================================================================== */
/*  Main Members page                                                  */
/* ================================================================== */
const LIMIT = 10; // API accepts 1–100
const SEARCH_DEBOUNCE_MS = 400;

/* ------------------------------------------------------------------ */
/*  Table grid — the header and every row share this ONE definition,
    so the columns can never drift apart.

    16 tracks: avatar 1 + member 4 + role 2 + status 3 + verified 2 +
    joined 2 + action 2.

    `minmax(0,1fr)` matters: a grid item's default minimum width is its
    content width, so without it a long email forces the track wider
    than its span and every following column slides out of alignment
    (and `truncate` silently does nothing). min-w-0 on the cell +
    truncate on the text is what keeps the row on one line.           */
const GRID_COLS = 'md:grid-cols-[repeat(16,minmax(0,1fr))]';

/* Page-number strip: 1 … 4 5 6 … 20 */
const pageWindow = (count, current) => {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const keep = new Set([1, count, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => keep.add(n));
  if (current >= count - 2)
    [count - 3, count - 2, count - 1].forEach((n) => keep.add(n));
  const sorted = [...keep]
    .filter((n) => n >= 1 && n <= count)
    .sort((a, b) => a - b);
  const out = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) out.push('…');
    out.push(n);
  });
  return out;
};

/* Sub-line under the status badge */
const statusSub = (p) => {
  if (p.state === 'blocked') {
    const when = relTime(p.updatedAt) || p.joinedAgo;
    return p.blockReason ? `“${p.blockReason}” · ${when}` : `Blocked · ${when}`;
  }
  if (p.state === 'invited') return `Invite sent ${p.joinedAgo}`;
  return `Joined ${p.joinedAgo}`;
};

export default function MemberList() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const [tab, setTab] = useState('All');
  const [query, setQuery] = useState('');
  /* Search is debounced before it hits the API as `search` */
  const [search, setSearch] = useState('');
  const [sortIdx, setSortIdx] = useState(0); // newest first (API default)
  const [sortOpen, setSortOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [stateModal, setStateModal] = useState(null); // { mode: 'block' | 'unblock', member }
  const [stateModalBusy, setStateModalBusy] = useState(false);
  const [stateModalError, setStateModalError] = useState('');

  /* Current page of rows + the pagination the API reported */
  const [people, setPeople] = useState([]);
  const [counts, setCounts] = useState({
    all: 0,
    invited: 0,
    active: 0,
    blocked: 0,
  });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  /* Bumped after invite / block / unblock to force a refetch even when
     page/tab/search didn't change */
  const [refreshTick, setRefreshTick] = useState(0);

  /* Guards against out-of-order responses (fast typing / tab switching) */
  const requestSeq = useRef(0);

  /* `showToast` is read through a ref so the fetch effect doesn't depend
     on it — if the toast context isn't memoised, a new function identity
     every render would retrigger the request in a loop. */
  const toastRef = useRef(showToast);
  useEffect(() => {
    toastRef.current = showToast;
  }, [showToast]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  /* Close the sort dropdown on outside click */
  const sortRef = useRef(null);
  useEffect(() => {
    if (!sortOpen) return undefined;
    const onDoc = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target))
        setSortOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [sortOpen]);

  /* ---------------- API helpers (same structure as Customers) ------ */
  const GetAPIcalls = useCallback(
    async (url) => {
      const token = localStorage.getItem('jwtToken');
      const apiUrl = `${BASE_URL}${url}`;

      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired.');
        }

        const errorData = await response.json().catch(() => ({
          message: 'Unexpected error',
        }));

        throw new Error(errorData.message);
      }

      return response.json();
    },
    [navigate],
  );

  const PostAPIcalls = useCallback(
    async (url, body) => {
      const token = localStorage.getItem('jwtToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${BASE_URL}${url}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'Unexpected error',
        }));
        throw new Error(errorData.message);
      }

      return response.json();
    },
    [navigate],
  );

  const PutAPIcalls = useCallback(
    async (url, body) => {
      const token = localStorage.getItem('jwtToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${BASE_URL}${url}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(body ?? {}),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'Unexpected error',
        }));
        throw new Error(errorData.message);
      }

      return response.json();
    },
    [navigate],
  );

  /* Build ?page=&limit=[&status=][&search=][&sort_by=][&sort_order=] */
  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(LIMIT));

    const status = FILTER_BY_TAB[tab];
    if (status && status !== 'all') params.set('status', status);

    if (search) params.set('search', search);

    const s = SORTS[sortIdx] ?? SORTS[0];
    params.set('sort_by', s.sort_by);
    params.set('sort_order', s.sort_order);

    return params.toString();
  }, [page, tab, search, sortIdx]);

  const getmembers = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/members?${buildQuery()}`,
      );

      if (response?.status == 401) {
        localStorage.removeItem('jwtToken');
        navigate('/login');
        return;
      }

      const rows = extractMembers(response).map(mapMember);
      const apiTotal = numFrom(response, [
        'total',
        'total_count',
        'totalCount',
      ]);
      const apiPages = numFrom(response, [
        'total_pages',
        'totalPages',
        'last_page',
      ]);
      const apiCounts = response?.data?.counts;

      if (seq !== requestSeq.current) return; // a newer request is in flight

      /* Fall back to an inferred total when the API omits it */
      const nextTotal =
        typeof apiTotal === 'number'
          ? apiTotal
          : (page - 1) * LIMIT + rows.length;
      const nextPages =
        typeof apiPages === 'number'
          ? apiPages
          : typeof apiTotal === 'number'
            ? Math.max(1, Math.ceil(apiTotal / LIMIT))
            : rows.length < LIMIT
              ? page
              : page + 1;

      setPeople(rows);
      setTotal(nextTotal);
      setTotalPages(Math.max(1, nextPages));
      if (apiCounts && typeof apiCounts === 'object') {
        setCounts({
          all: apiCounts.all ?? 0,
          invited: apiCounts.invited ?? 0,
          active: apiCounts.active ?? 0,
          blocked: apiCounts.blocked ?? 0,
        });
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      toastRef.current(err.message, 'error');
      setPeople([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [GetAPIcalls, buildQuery, navigate, page, refreshTick]);

  /* Any change to page / status / search / sort — or a refresh tick
     after invite / block / unblock — refetches */
  useEffect(() => {
    getmembers();
  }, [getmembers]);

  /* If a refetch shrinks the result set below the page we're on
     (blocked row leaving the Active tab, narrower search), step back
     onto the last real page. */
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageCount = Math.max(1, totalPages);
  const safePage = Math.min(page, pageCount);
  const rangeStart = people.length ? (safePage - 1) * LIMIT + 1 : 0;
  const rangeEnd = (safePage - 1) * LIMIT + people.length;

  const currentSort = SORTS[sortIdx] ?? SORTS[0];
  const isDefaultSort = sortIdx === 0;

  const currentFilters = { tab, query, isDefaultSort };

  const gotoPage = (p) => setPage(Math.max(1, Math.min(p, pageCount)));

  /* ---------------- Invite → POST /invite ----------------
     The modal collects the fields, validates and calls onInvite
     (POST /api/v1/agency/members/invite). It shows the one-time
     temp_password in its success step; the parent only learns the
     invite succeeded (no password ever reaches list state). */
  const inviteMember = useCallback(
    (payload) => PostAPIcalls('/api/v1/agency/members/invite', payload),
    [PostAPIcalls],
  );

  const handleInviteClosed = (wasSuccess) => {
    setModalOpen(false);
    if (!wasSuccess) return;
    /* Land on the Invited tab so the new member is visible */
    setQuery('');
    setSearch('');
    setPage(1);
    setTab('Invited');
    setRefreshTick((t) => t + 1);
  };

  /* ---------------- Block / Unblock → PUT endpoints ---------------- */
  const openBlock = (member) => {
    setStateModalError('');
    setStateModal({ mode: 'block', member });
  };

  const openUnblock = (member) => {
    setStateModalError('');
    setStateModal({ mode: 'unblock', member });
  };

  const handleStateSubmit = async (reason) => {
    if (!stateModal) return;
    const { mode, member } = stateModal;
    setStateModalBusy(true);
    setStateModalError('');
    try {
      if (mode === 'block') {
        /* PUT /api/v1/agency/members/{user_id}/block — reason required */
        const res = await PutAPIcalls(
          `/api/v1/agency/members/${member.user_id}/block`,
          { reason },
        );
        toastRef.current(
          res?.message || 'Admin blocked successfully',
          'success',
        );
      } else {
        /* PUT /api/v1/agency/members/{user_id}/unblock — body optional */
        const body = reason ? { reason } : {};
        const res = await PutAPIcalls(
          `/api/v1/agency/members/${member.user_id}/unblock`,
          body,
        );
        toastRef.current(
          res?.message || 'Admin unblocked successfully',
          'success',
        );
      }
      setStateModal(null);
      setRefreshTick((t) => t + 1); // server truth wins — refetch
    } catch (err) {
      setStateModalError(err.message); // shown inside the modal
    } finally {
      setStateModalBusy(false);
    }
  };

  const stateColorFor = (p) =>
    p.isOwner ? '#375AE7' : (STATE_COLOR[p.state] ?? '#375AE7');
  const tintFor = (p) => {
    const c = stateColorFor(p);
    return `${c}1A`; // ~10% tint
  };

  return (
    <div className="min-h-screen bg-white rounded-2xl text-slate-900 font-sans lg:min-w-[78vw] lg:max-w-[80vw]">
      <main className="px-4 md:px-6 lg:px-8 pt-6 md:pt-8 pb-14 mx-auto">
        {/* ---------- Hero header band ---------- */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1B2668] via-[#375AE7] to-[#5B7CFA] shadow-lift">
          <div className="absolute -top-24 right-10 w-80 h-80 rounded-full bg-white/10 blur-3xl animate-drift pointer-events-none" />
          <div className="absolute -bottom-28 -left-16 w-80 h-80 rounded-full bg-[#22D3EE]/20 blur-3xl animate-drift-slow pointer-events-none" />

          <div className="relative p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1 min-w-0">
              <h1 className="mt-1.5 text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-2.5">
                Members
                <span className="w-9 h-9 rounded-xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center">
                  <Users size={18} strokeWidth={1.9} className="text-white" />
                </span>
              </h1>
              <p className="mt-1.5 text-sm text-white/75">
                {counts.all} members · {counts.active} active · {counts.invited}{' '}
                invited · {counts.blocked} blocked
              </p>
            </div>

            <div className="flex items-center gap-2.5 shrink-0">
              <button
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white text-brand text-sm font-bold shadow-md hover:bg-white/90 transition-all active:scale-[0.97]"
              >
                <UserPlus size={16} />
                Invite member
              </button>
            </div>
          </div>
        </div>

        {/* ---------- Summary cards (clickable status filters) ----------
            Driven by data.counts — the API computes them over ALL
            members, ignoring search and the selected tab, so they stay
            steady while someone types.                                */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CARDS.map((c) => {
            const Icon = c.icon;
            const activeTabFilter = FILTER_BY_TAB[tab];
            const active = activeTabFilter === c.key;
            return (
              <button
                key={c.key}
                onClick={() => {
                  setPage(1);
                  setTab(c.tab);
                }}
                className={`relative overflow-hidden text-left rounded-xl border p-4 transition-all duration-200 hover:-translate-y-0.5 ${
                  active
                    ? 'border-transparent shadow-card'
                    : 'border-slate-200 bg-white shadow-sm hover:shadow-card'
                }`}
                style={
                  active
                    ? {
                        boxShadow: `0 18px 34px -22px ${c.color}99`,
                        background: c.tint,
                      }
                    : undefined
                }
              >
                <span
                  className="absolute left-0 top-0 bottom-0 w-1 rounded-r-full"
                  style={{ background: active ? c.color : 'transparent' }}
                />
                <div className="flex items-center gap-3">
                  <span
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: active ? c.tint : '#F1F5F9',
                      color: active ? c.color : '#64748B',
                    }}
                  >
                    <Icon size={18} strokeWidth={1.9} />
                  </span>
                  <div className="min-w-0">
                    <p
                      className="text-xl font-bold tabular-nums leading-6"
                      style={{ color: active ? c.color : '#0F172A' }}
                    >
                      {counts[c.key]}
                    </p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {c.label}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* ---------- Filter row ---------- */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Pill tabs with live badges from data.counts */}
          <div className="inline-flex items-center gap-1 rounded-full bg-white p-1 ring-1 ring-slate-200 shadow-sm w-fit overflow-x-auto">
            {TABS.map((t) => {
              const active = tab === t;
              const count = counts[FILTER_BY_TAB[t]] ?? 0;
              return (
                <button
                  key={t}
                  onClick={() => {
                    setPage(1);
                    setTab(t);
                  }}
                  className={`relative inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap ${
                    active
                      ? 'bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] text-white shadow-md shadow-brand/25'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {t}
                  <span
                    className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums ${
                      active
                        ? 'bg-white/20 text-white'
                        : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2.5">
            {/* Sort — sort_by + sort_order */}
            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setSortOpen((o) => !o)}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50 transition"
              >
                <SlidersHorizontal size={14} className="text-slate-400" />
                <span className="hidden sm:inline">{currentSort.label}</span>
                <ChevronDown
                  size={14}
                  className={`text-slate-400 transition-transform duration-200 ${sortOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {sortOpen && (
                <div className="absolute right-0 mt-2 w-52 rounded-xl bg-white ring-1 ring-slate-200 shadow-lift p-1.5 z-20">
                  {SORTS.map((s, i) => (
                    <button
                      key={s.label}
                      onClick={() => {
                        setPage(1);
                        setSortIdx(i);
                        setSortOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        i === sortIdx
                          ? 'bg-brand/[0.06] text-brand font-semibold'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {s.label}
                      {i === sortIdx && <Check size={14} />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search — name, email or mobile (case insensitive) */}
            <div className="relative">
              <Search
                size={15}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={query}
                onChange={(e) => {
                  setPage(1);
                  setQuery(e.target.value);
                }}
                placeholder="Search name, email, mobile…"
                className="h-11 pl-10 pr-4 w-full sm:w-64 rounded-full border border-slate-200 bg-white text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition"
              />
            </div>
          </div>
        </div>

        {/* ---------- Active filter chips (what's currently applied) ---------- */}
        {hasActiveFilters(currentFilters) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <SlidersHorizontal size={11} />
              Active filters
            </span>
            {tab !== 'All' && (
              <FilterChip
                label={`Status: ${tab}`}
                onClear={() => {
                  setTab('All');
                  setPage(1);
                }}
              />
            )}
            {!isDefaultSort && (
              <FilterChip
                label={`Sort: ${currentSort.label}`}
                onClear={() => {
                  setSortIdx(0);
                  setPage(1);
                }}
              />
            )}
            {query.trim() !== '' && (
              <FilterChip
                label={`Search: “${query.trim()}”`}
                onClear={() => {
                  setQuery('');
                  setPage(1);
                }}
              />
            )}
            <button
              onClick={() => {
                setTab('All');
                setQuery('');
                setSearch('');
                setSortIdx(0);
                setPage(1);
              }}
              className="h-7 px-2 rounded-lg text-[11px] font-semibold text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            >
              Clear all
            </button>
          </div>
        )}

        {/* ---------- Members table (card rows) ---------- */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200/70 shadow-card overflow-hidden">
          {/* Column header */}
          <div
            className={`hidden md:grid ${GRID_COLS} gap-3 px-5 py-3 bg-gradient-to-r from-[#FAFBFF] to-[#F1F5FC] border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wide text-slate-400`}
          >
            <span className="md:col-span-1" />
            <span className="md:col-span-4">Member</span>
            <span className="md:col-span-2">Role</span>
            <span className="md:col-span-3">Status</span>
            <span className="md:col-span-2">Verified</span>
            <span className="md:col-span-2">Joined</span>
            <span className="md:col-span-2 text-right">Action</span>
          </div>

          {/* Rows */}
          <div
            className={`divide-y divide-slate-100 transition-opacity duration-200 ${loading && people.length > 0 ? 'opacity-60 pointer-events-none' : ''}`}
            aria-busy={loading}
          >
            {/* Skeleton rows on first load */}
            {loading &&
              people.length === 0 &&
              [0, 1, 2, 3, 4].map((i) => (
                <div
                  key={`sk-${i}`}
                  className={`grid grid-cols-1 ${GRID_COLS} items-center gap-3 px-5 py-4`}
                >
                  <div className="md:col-span-1">
                    <span className="block w-10 h-10 rounded-full bg-slate-100 animate-pulse" />
                  </div>
                  <div className="md:col-span-4 space-y-1.5">
                    <span className="block h-3.5 w-32 rounded bg-slate-100 animate-pulse" />
                    <span className="block h-2.5 w-44 rounded bg-slate-100 animate-pulse" />
                  </div>
                  <div className="md:col-span-2">
                    <span className="block h-5 w-14 rounded-md bg-slate-100 animate-pulse" />
                  </div>
                  <div className="md:col-span-3 space-y-1.5">
                    <span className="block h-5 w-20 rounded-full bg-slate-100 animate-pulse" />
                    <span className="block h-2.5 w-28 rounded bg-slate-100 animate-pulse" />
                  </div>
                  <div className="md:col-span-2 space-y-1.5">
                    <span className="block h-4 w-16 rounded-md bg-slate-100 animate-pulse" />
                    <span className="block h-4 w-16 rounded-md bg-slate-100 animate-pulse" />
                  </div>
                  <div className="md:col-span-2 space-y-1.5">
                    <span className="block h-3 w-20 rounded bg-slate-100 animate-pulse" />
                    <span className="block h-2.5 w-14 rounded bg-slate-100 animate-pulse" />
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <span className="block h-8 w-20 rounded-lg bg-slate-100 animate-pulse" />
                  </div>
                </div>
              ))}

            {people.map((p, idx) => {
              const color = stateColorFor(p);
              return (
                <div
                  key={p.user_id ?? p.id ?? idx}
                  className={`group relative grid grid-cols-1 ${GRID_COLS} items-center gap-3 px-5 py-4 transition-all duration-200 hover:bg-[#F8FAFF]`}
                  style={{
                    animation: `fade-up 480ms ${idx * 45}ms cubic-bezier(.22,1,.36,1) both`,
                  }}
                >
                  {/* state accent rail (always visible) */}
                  <span
                    className="absolute left-0 top-0 bottom-0 w-1 rounded-r-full"
                    style={{ background: color }}
                  />

                  {/* Avatar — tinted by member state (owner = brand) */}
                  <div className="md:col-span-1 min-w-0 md:text-center">
                    <span
                      className="relative inline-flex w-10 h-10 rounded-full items-center justify-center text-[11px] font-bold ring-2 ring-white transition-transform duration-200 group-hover:scale-110"
                      style={{ background: tintFor(p), color }}
                    >
                      {p.initial}
                    </span>
                  </div>

                  {/* Member — name, email, mobile */}
                  <div className="md:col-span-4 min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <p className="truncate text-sm font-semibold text-slate-800">
                        {p.name}
                      </p>
                      {p.isOwner && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 ring-1 ring-amber-200">
                          <ShieldCheck size={9} />
                          Owner
                        </span>
                      )}
                    </div>
                    <p className="truncate text-[11px] text-slate-400">
                      {p.email}
                    </p>
                    <p className="truncate text-[11px] text-slate-400 flex items-center gap-1">
                      <Phone size={9} className="shrink-0" />
                      {p.mobile}
                    </p>
                  </div>

                  {/* Role — the ONE badge (owner vs admin) */}
                  <div className="md:col-span-2 min-w-0">
                    {p.isOwner ? (
                      <span className="inline-flex max-w-full items-center gap-1 text-[10px] font-semibold rounded-md px-2 py-1 ring-1 bg-amber-50 text-amber-700 ring-amber-200">
                        <ShieldCheck size={10} className="shrink-0" />
                        <span className="truncate">Owner</span>
                      </span>
                    ) : (
                      <span className="inline-flex max-w-full items-center gap-1 text-[10px] font-semibold rounded-md px-2 py-1 ring-1 bg-brand/10 text-brand ring-brand/20">
                        <UserCheck size={10} className="shrink-0" />
                        <span className="truncate">Admin</span>
                      </span>
                    )}
                  </div>

                  {/* Status — member_state from the API */}
                  <div className="md:col-span-3 min-w-0">
                    <span
                      className={`inline-flex max-w-full items-center gap-1.5 text-[10px] font-bold rounded-full px-2 py-0.5 ring-1 ${STATUS_BADGE[p.state] ?? STATUS_BADGE.invited}`}
                    >
                      <span className="h-1.5 w-1.5 shrink-0 animate-pulse-dot rounded-full bg-current" />
                      <span className="truncate">{p.stateText}</span>
                    </span>
                    <p
                      className="mt-1 truncate text-[10px] text-slate-400"
                      title={statusSub(p)}
                    >
                      {statusSub(p)}
                    </p>
                  </div>

                  {/* Verified — email_verified / mobile_number_verified */}
                  <div className="md:col-span-2 min-w-0 space-y-1">
                    {[
                      { ok: p.emailVerified, Icon: Mail, label: 'Email' },
                      { ok: p.mobileVerified, Icon: Phone, label: 'Mobile' },
                    ].map(({ ok, Icon, label }) => (
                      <span
                        key={label}
                        title={`${label} ${ok ? 'verified' : 'not verified'}`}
                        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold ring-1 mr-1 ${
                          ok
                            ? 'bg-emerald-50 text-emerald-600 ring-emerald-200'
                            : 'bg-slate-100 text-slate-400 ring-slate-200'
                        }`}
                      >
                        <Icon size={9} className="shrink-0" />
                        {label}
                        {ok ? (
                          <Check size={9} strokeWidth={3} />
                        ) : (
                          <X size={9} strokeWidth={3} />
                        )}
                      </span>
                    ))}
                  </div>

                  {/* Joined */}
                  <div className="md:col-span-2 min-w-0">
                    <p className="truncate text-xs font-semibold text-slate-600">
                      {p.joined}
                    </p>
                    <p className="truncate text-[10px] text-slate-400">
                      {p.joinedAgo}
                    </p>
                  </div>

                  {/* Action — block / unblock (owner can never be blocked:
                      the controller's owner-only check would refuse it) */}
                  <div className="md:col-span-2 flex min-w-0 items-center justify-end gap-1.5">
                    {p.isOwner ? (
                      <span
                        title="The business owner can't be blocked"
                        className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-100 bg-slate-50 px-2.5 text-[11px] font-semibold text-slate-300"
                      >
                        <ShieldCheck size={13} />
                        Owner
                      </span>
                    ) : p.state === 'blocked' ? (
                      <button
                        onClick={() => openUnblock(p)}
                        className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[11px] font-semibold transition-all duration-200 active:scale-95 text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
                      >
                        <Unlock size={13} /> Unblock
                      </button>
                    ) : (
                      <button
                        onClick={() => openBlock(p)}
                        className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[11px] font-semibold transition-all duration-200 active:scale-95 text-red-600 bg-white border-red-200 hover:bg-red-50"
                      >
                        <Lock size={13} /> Block
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {people.length === 0 && !loading && (
              <div className="py-16 text-center">
                <Users size={30} className="mx-auto text-slate-300" />
                <p className="text-sm text-slate-500 mt-3">
                  No members match your filters
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ---------- Pagination (server-driven: page / limit) ---------- */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[11px] text-slate-400">
            Showing{' '}
            <b className="text-slate-600">
              {rangeStart}–{rangeEnd}
            </b>{' '}
            of <b className="text-slate-600">{total}</b> members · {LIMIT} per
            page
          </p>

          {pageCount > 1 && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => gotoPage(safePage - 1)}
                disabled={safePage === 1}
                className="h-9 px-3 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              {pageWindow(pageCount, safePage).map((p, i) =>
                p === '…' ? (
                  <span
                    key={`gap-${i}`}
                    className="h-9 w-9 inline-flex items-center justify-center text-xs font-bold text-slate-300 select-none"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => gotoPage(p)}
                    className={`h-9 w-9 rounded-lg text-xs font-bold transition-all duration-200 ${
                      safePage === p
                        ? 'bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] text-white shadow-md shadow-brand/25'
                        : 'text-slate-600 border border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                onClick={() => gotoPage(safePage + 1)}
                disabled={safePage === pageCount}
                className="h-9 px-3 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </main>

      {/* ---------- Invite member modal (POST /invite + one-time temp password) ---------- */}
      <InviteMemberModal
        open={modalOpen}
        onClose={handleInviteClosed}
        onInvite={inviteMember}
      />

      {/* ---------- Block / Unblock modal (PUT endpoints, reason) ---------- */}
      <BlockMemberModal
        open={!!stateModal}
        mode={stateModal?.mode ?? 'block'}
        member={stateModal?.member ?? null}
        busy={stateModalBusy}
        error={stateModalError}
        onClose={() => setStateModal(null)}
        onSubmit={handleStateSubmit}
      />
    </div>
  );
}
